import React, { useState, useRef, ChangeEvent } from 'react';
import { loadAll, dump } from 'js-yaml';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    makeStyles,
    MenuItem,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Content, Header, Page } from '@backstage/core-components';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SystemLink {
    url: string;
    title: string;
    icon?: string;
}

interface BsSystem {
    apiVersion: string;
    kind: 'System';
    metadata: {
        name: string;
        title?: string;
        description?: string;
        annotations: Record<string, string>;
        links: SystemLink[];
    };
    spec: {
        owner: string;
        lifecycle: string;
        domain: string;
    };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ANNO_PREFIX = 'itkom.pbs.ch/';

const PHASES = ['poc', 'dev', 'production', 'end-of-life'] as const;
type Phase = (typeof PHASES)[number];

const PHASE_LABELS: Record<Phase, string> = {
    poc: 'PoC',
    dev: 'Dev',
    production: 'Production',
    'end-of-life': 'End-of-Life',
};

const PHASE_COLORS: Record<Phase, string> = {
    poc: '#FF9800',
    dev: '#2196F3',
    production: '#4CAF50',
    'end-of-life': '#9E9E9E',
};

const BS_LIFECYCLES = ['experimental', 'production', 'deprecated'];
const DOMAINS = [
    'mitgliederverwaltung',
    'kurs-lager-ausbildung',
    'kommunikation',
    'infrastruktur',
    'information-dokumente',
    'silverscouts',
];
const OWNERS = [
    'group:default/itkom',
    'group:default/gspbs',
    'group:default/team-midata',
    'group:default/ecamp-v3-team',
    'user:default/filou',
    'user:default/nano',
    'user:default/clever',
    'user:default/folletta',
    'user:default/papillon',
    'user:default/mica',
    'user:default/gamschi',
    'user:default/trick',
    'user:default/cosinus',
    'user:default/kodack',
    'user:default/aurea',
    'user:default/floh',
];

const ITKOM_ANNO_PREFIX = 'itkom.pbs.ch/';
const CRITICALITY_OPTIONS = ['low', 'medium', 'high'];
const DEV_MODE_OPTIONS = ['internal', 'external', 'open_source'];
const OPS_MODE_OPTIONS = ['internal', 'external', 'betriebsplattform'];
const SLA_OPTIONS = ['true', 'false'];

const ROLE_ANNO_PREFIX = 'itkom.pbs.ch/role-';
const KNOWN_ROLES = [
    'project-lead', 'project-member', 'developer', 'operator', 'steward',
    'content-owner', 'implementer', 'oversight', 'commission', 'supplier',
    'client', 'user', 'original-developer', 'owner',
];

const LINK_ICONS = [
    'website', 'diagram', 'spreadsheet', 'bug',
    'monitor', 'docs', 'dashboard', 'build', 'security', 'cloud',
];

interface AnnoDef {
    key: string;
    label: string;
    options: string[];
}

/**
 * Annotation definitions per lifecycle phase, based on README.
 * Each annotation is listed in the phase where it is first introduced.
 */
const ANNO_GROUPS: Record<Phase, AnnoDef[]> = {
    poc: [
        { key: 'scope-documented', label: 'Scope Documented', options: ['true', 'pending'] },
        { key: 'poc-validated', label: 'PoC Validated', options: ['true', 'pending'] },
        { key: 'dsfa-status', label: 'DSFA Status', options: ['completed', 'not-required', 'pending'] },
        { key: 'architecture-diagram', label: 'Architecture Diagram', options: ['completed', 'pending'] },
        { key: 'portmatrix', label: 'Port Matrix', options: ['completed', 'pending'] },
        { key: 'go-nogo', label: 'Go/No-Go Decision', options: ['approved', 'pending'] },
    ],
    dev: [
        { key: 'health-endpoint', label: 'Health Endpoint', options: ['true', 'n/a'] },
        { key: 'monitoring-status', label: 'Monitoring Status', options: ['basic', 'active', 'pending'] },
        { key: 'alerting-status', label: 'Alerting Status', options: ['active', 'pending'] },
        { key: 'cost-budget', label: 'Cost Budget', options: ['active', 'pending'] },
        { key: 'dependencies-current', label: 'Dependencies Current', options: ['true', 'false'] },
        { key: 'usecase-validated', label: 'Use Case Validated', options: ['true', 'pending'] },
    ],
    production: [
        { key: 'ops-owner', label: 'Ops Owner', options: ['assigned', 'pending'] },
        { key: 'usage-report', label: 'Usage Report', options: ['active', 'n/a', 'pending'] },
        { key: 'quarterly-review', label: 'Quarterly Review', options: ['true', 'pending'] },
    ],
    'end-of-life': [
        { key: 'data-archived', label: 'Data Archived', options: ['true', 'pending'] },
        { key: 'azure-cleanup', label: 'Azure Cleanup', options: ['true', 'pending'] },
        { key: 'secrets-removed', label: 'Secrets Removed', options: ['true', 'pending'] },
        { key: 'repo-archived', label: 'Repo Archived', options: ['true', 'pending'] },
    ],
};

const DEFAULT_SYSTEM: BsSystem = {
    apiVersion: 'backstage.io/v1alpha1',
    kind: 'System',
    metadata: {
        name: 'new-system',
        title: 'New System',
        description: 'Description of the new system\n',
        annotations: {
            'itkom.pbs.ch/lifecycle-phase': 'poc',
            'itkom.pbs.ch/dsfa-status': 'pending',
            'itkom.pbs.ch/architecture-diagram': 'pending',
            'itkom.pbs.ch/portmatrix': 'pending',
            'itkom.pbs.ch/health-endpoint': 'true',
            'itkom.pbs.ch/monitoring-status': 'pending',
            'itkom.pbs.ch/alerting-status': 'pending',
            'itkom.pbs.ch/cost-budget': 'pending',
            'itkom.pbs.ch/dependencies-current': 'false',
            'itkom.pbs.ch/ops-owner': 'pending',
            'itkom.pbs.ch/usage-report': 'pending',
            'itkom.pbs.ch/criticality': 'low',
            'itkom.pbs.ch/development-mode': 'internal',
            'itkom.pbs.ch/operations-mode': 'external',
            'itkom.pbs.ch/has-sla': 'false',
        },
        links: [],
    },
    spec: {
        owner: 'group:default/itkom',
        lifecycle: 'experimental',
        domain: 'infrastruktur',
    },
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
    uploadWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '55vh',
        gap: theme.spacing(3),
    },
    uploadPaper: {
        padding: theme.spacing(5),
        textAlign: 'center',
        maxWidth: 480,
        width: '100%',
        border: `2px dashed ${theme.palette.divider}`,
        cursor: 'pointer',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
    headerBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    headerActions: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    systemCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    cardContent: {
        flexGrow: 1,
    },
    cardActions: {
        justifyContent: 'flex-end',
        paddingTop: 0,
    },
    tabContent: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
    alwaysBox: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    activeAccordion: {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    accordionDetails: {
        width: '100%',
    },
    linkRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    sectionLabel: {
        fontWeight: 600,
        marginBottom: theme.spacing(1.5),
    },
    hiddenInput: {
        display: 'none',
    },
    phaseChipRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeSystem(raw: any): BsSystem {
    const annotations: Record<string, string> = {};
    if (raw?.metadata?.annotations) {
        for (const [k, v] of Object.entries(raw.metadata.annotations)) {
            annotations[String(k)] = String(v);
        }
    }
    const rawLinks: any[] = raw?.metadata?.links ?? [];
    return {
        apiVersion: raw?.apiVersion ?? 'backstage.io/v1alpha1',
        kind: 'System',
        metadata: {
            name: String(raw?.metadata?.name ?? ''),
            title: raw?.metadata?.title != null ? String(raw.metadata.title) : undefined,
            description: raw?.metadata?.description != null ? String(raw.metadata.description) : undefined,
            annotations,
            links: rawLinks.map((l: any) => ({
                url: String(l?.url ?? ''),
                title: String(l?.title ?? ''),
                ...(l?.icon != null ? { icon: String(l.icon) } : {}),
            })),
        },
        spec: {
            owner: String(raw?.spec?.owner ?? 'group:default/itkom'),
            lifecycle: String(raw?.spec?.lifecycle ?? 'production'),
            domain: String(raw?.spec?.domain ?? 'infrastruktur'),
        },
    };
}

function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

function generateYaml(systems: BsSystem[]): string {
    return systems
        .map(s => '---\n' + dump(s, { lineWidth: -1, noRefs: true, quotingType: "'" }))
        .join('');
}

// ─── TabPanel ─────────────────────────────────────────────────────────────────

function TabPanel({ children, value, index }: {
    children: React.ReactNode;
    value: number;
    index: number;
}) {
    return value === index ? <>{children}</> : null;
}

// ─── SystemEditorPage ─────────────────────────────────────────────────────────

export function SystemEditorPage() {
    const classes = useStyles();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [systems, setSystems] = useState<BsSystem[]>([]);
    const [hasFile, setHasFile] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editSystem, setEditSystem] = useState<BsSystem | null>(null);
    const [tabIndex, setTabIndex] = useState(0);

    // ── File handling ──────────────────────────────────────────────

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = evt => {
            const content = evt.target?.result as string;
            const docs: any[] = [];
            loadAll(content, doc => docs.push(doc));
            setSystems(docs.filter(d => d?.kind === 'System').map(normalizeSystem));
            setHasFile(true);
        };
        reader.readAsText(file);
        // Reset so same file can be re-loaded
        e.target.value = '';
    };

    const handleDownload = () => {
        const content = generateYaml(systems);
        const blob = new Blob([content], { type: 'text/yaml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'system.yaml';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // ── Dialog open/close ──────────────────────────────────────────

    const openEdit = (index: number) => {
        setEditIndex(index);
        setEditSystem(deepClone(systems[index]));
        setTabIndex(0);
    };

    const openNew = () => {
        setEditIndex(-1);
        setEditSystem(deepClone(DEFAULT_SYSTEM));
        setTabIndex(0);
    };

    const closeEdit = () => {
        setEditIndex(null);
        setEditSystem(null);
    };

    const saveEdit = () => {
        if (!editSystem) return;
        setSystems(prev => {
            const next = [...prev];
            if (editIndex === -1) {
                next.push(editSystem);
            } else if (editIndex !== null) {
                next[editIndex] = editSystem;
            }
            return next;
        });
        closeEdit();
    };

    const deleteSystem = (index: number) =>
        setSystems(prev => prev.filter((_, i) => i !== index));

    // ── Edit helpers ───────────────────────────────────────────────

    const setFieldValue = (path: string[], value: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            let obj: any = next;
            for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
            obj[path[path.length - 1]] = value;
            return next;
        });
    };

    const setAnno = (key: string, value: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            next.metadata.annotations[ANNO_PREFIX + key] = value;
            return next;
        });
    };

    const getAnno = (key: string): string =>
        editSystem?.metadata?.annotations?.[ANNO_PREFIX + key] ?? '';

    const setItkomAnno = (key: string, value: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            next.metadata.annotations[ITKOM_ANNO_PREFIX + key] = value;
            return next;
        });
    };

    const getItkomAnno = (key: string): string =>
        editSystem?.metadata?.annotations?.[ITKOM_ANNO_PREFIX + key] ?? '';

    const updateLink = (i: number, field: keyof SystemLink, value: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            (next.metadata.links[i] as any)[field] = value;
            return next;
        });
    };

    const addLink = () => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            next.metadata.links.push({ url: '', title: '', icon: 'website' });
            return next;
        });
    };

    const removeLink = (i: number) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            next.metadata.links.splice(i, 1);
            return next;
        });
    };

    const getRoleAnnotations = (): Array<{ roleSuffix: string; value: string }> => {
        if (!editSystem) return [];
        return Object.entries(editSystem.metadata.annotations)
            .filter(([k]) => k.startsWith(ROLE_ANNO_PREFIX))
            .map(([k, v]) => ({ roleSuffix: k.slice(ROLE_ANNO_PREFIX.length), value: v }));
    };

    const setRoleAnno = (roleSuffix: string, value: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            next.metadata.annotations[`${ROLE_ANNO_PREFIX}${roleSuffix}`] = value;
            return next;
        });
    };

    const renameRoleAnno = (oldSuffix: string, newSuffix: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            const value = next.metadata.annotations[`${ROLE_ANNO_PREFIX}${oldSuffix}`] ?? '';
            delete next.metadata.annotations[`${ROLE_ANNO_PREFIX}${oldSuffix}`];
            next.metadata.annotations[`${ROLE_ANNO_PREFIX}${newSuffix}`] = value;
            return next;
        });
    };

    const removeRoleAnno = (roleSuffix: string) => {
        setEditSystem(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            delete next.metadata.annotations[`${ROLE_ANNO_PREFIX}${roleSuffix}`];
            return next;
        });
    };

    const addRoleAnno = () => {
        const used = new Set(getRoleAnnotations().map(r => r.roleSuffix));
        const newRole = KNOWN_ROLES.find(r => !used.has(r)) ?? 'project-lead';
        setRoleAnno(newRole, '');
    };

    const currentPhase = (
        editSystem?.metadata?.annotations?.[`${ANNO_PREFIX}lifecycle-phase`] ?? 'poc'
    ) as Phase;

    // ── Upload screen ──────────────────────────────────────────────

    if (!hasFile) {
        return (
            <Page themeId="tool">
                <Header
                    title="System.yaml Editor"
                    subtitle="Upload and edit Backstage system definitions"
                />
                <Content>
                    <div className={classes.uploadWrap}>
                        <Paper
                            className={classes.uploadPaper}
                            onClick={() => fileInputRef.current?.click()}
                            elevation={0}
                        >
                            <CloudUploadIcon style={{ fontSize: 72, color: '#bdbdbd' }} />
                            <Typography variant="h5" style={{ marginTop: 16 }}>
                                Upload system.yaml
                            </Typography>
                            <Typography variant="body1" color="textSecondary" style={{ marginTop: 8 }}>
                                Select your <code>system.yaml</code> file to start editing
                            </Typography>
                            <Box mt={3}>
                                <Button variant="contained" color="primary" startIcon={<CloudUploadIcon />}>
                                    Choose File
                                </Button>
                            </Box>
                        </Paper>
                        <Typography variant="body2" color="textSecondary">— or —</Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => { setHasFile(true); setSystems([]); }}
                        >
                            Start with Empty File
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".yaml,.yml"
                            aria-label="Upload system.yaml file"
                            title="Upload system.yaml file"
                            className={classes.hiddenInput}
                            onChange={handleFileChange}
                        />
                    </div>
                </Content>
            </Page>
        );
    }

    // ── Editor screen ──────────────────────────────────────────────

    return (
        <Page themeId="tool">
            <Header
                title="System.yaml Editor"
                subtitle={`${systems.length} system${systems.length !== 1 ? 's' : ''} loaded`}
            />
            <Content>
                <Box className={classes.headerBar}>
                    <Typography variant="h6">Systems</Typography>
                    <Box className={classes.headerActions}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Load File
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={openNew}
                        >
                            Add System
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<GetAppIcon />}
                            onClick={handleDownload}
                            disabled={systems.length === 0}
                        >
                            Download YAML
                        </Button>
                    </Box>
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".yaml,.yml"
                    aria-label="Upload system.yaml file"
                    title="Upload system.yaml file"
                    className={classes.hiddenInput}
                    onChange={handleFileChange}
                />

                {systems.length === 0 ? (
                    <Box textAlign="center" mt={8}>
                        <Typography variant="h6" color="textSecondary">
                            No systems yet. Click "Add System" to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {systems.map((system, idx) => {
                            const phase = (
                                system.metadata.annotations?.[`${ANNO_PREFIX}lifecycle-phase`] ?? 'poc'
                            ) as Phase;
                            const phaseColor = PHASE_COLORS[phase] ?? '#9E9E9E';
                            return (
                                <Grid item xs={12} sm={6} md={4} key={`${system.metadata.name}-${idx}`}>
                                    <Card className={classes.systemCard}>
                                        <CardHeader
                                            title={system.metadata.title ?? system.metadata.name}
                                            subheader={system.metadata.name}
                                            titleTypographyProps={{ variant: 'subtitle1' }}
                                            subheaderTypographyProps={{ variant: 'caption' }}
                                        />
                                        <CardContent className={classes.cardContent}>
                                            <Box mb={1} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                                                <Chip
                                                    label={PHASE_LABELS[phase] ?? phase}
                                                    size="small"
                                                    style={{ backgroundColor: phaseColor, color: '#fff' }}
                                                />
                                                <Typography variant="caption" color="textSecondary">
                                                    {system.spec.domain} · {system.spec.owner}
                                                </Typography>
                                            </Box>
                                            {system.metadata.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    style={{
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                    } as React.CSSProperties}
                                                >
                                                    {system.metadata.description.trim()}
                                                </Typography>
                                            )}
                                            {(() => {
                                                const parties = Object.entries(system.metadata.annotations)
                                                    .filter(([k]) => k.startsWith(ROLE_ANNO_PREFIX))
                                                    .flatMap(([k, v]) => {
                                                        const suffix = k.slice(ROLE_ANNO_PREFIX.length);
                                                        const role = suffix.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                                        return v.split(',').map(ref => `${ref.trim().split('/').pop() ?? ref.trim()} (${role})`);
                                                    });
                                                if (parties.length === 0) return null;
                                                return (
                                                    <Box mt={1}>
                                                        {parties.slice(0, 3).map((label, i) => (
                                                            <Typography key={i} variant="caption" display="block" color="textSecondary">
                                                                {label}
                                                            </Typography>
                                                        ))}
                                                        {parties.length > 3 && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                +{parties.length - 3} more
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </CardContent>
                                        <CardActions className={classes.cardActions}>
                                            <Tooltip title="Delete system">
                                                <IconButton size="small" onClick={() => deleteSystem(idx)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<EditIcon />}
                                                onClick={() => openEdit(idx)}
                                            >
                                                Edit
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {/* ── Edit Dialog ─────────────────────────────────────── */}
                <Dialog
                    open={editSystem !== null}
                    onClose={closeEdit}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {editIndex === -1
                            ? 'New System'
                            : `Edit: ${editSystem?.metadata?.title ?? editSystem?.metadata?.name}`}
                    </DialogTitle>

                    <DialogContent dividers style={{ padding: '0 24px' }}>
                        <Tabs
                            value={tabIndex}
                            onChange={(_e, v) => setTabIndex(v)}
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab label="Basic Info" />
                            <Tab label="Spec" />
                            <Tab label="Annotations" />
                            <Tab label="Links" />
                        </Tabs>
                        <Divider />

                        {/* ── Tab: Basic Info ──────────────────────────── */}
                        <TabPanel value={tabIndex} index={0}>
                            <Box className={classes.tabContent}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Name"
                                            value={editSystem?.metadata?.name ?? ''}
                                            onChange={e => setFieldValue(['metadata', 'name'], e.target.value)}
                                            fullWidth
                                            required
                                            helperText="Unique identifier (kebab-case)"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Title"
                                            value={editSystem?.metadata?.title ?? ''}
                                            onChange={e => setFieldValue(['metadata', 'title'], e.target.value)}
                                            fullWidth
                                            helperText="Human-readable display name"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Description"
                                            value={(editSystem?.metadata?.description ?? '').trimEnd()}
                                            onChange={e =>
                                                setFieldValue(['metadata', 'description'], `${e.target.value}\n`)
                                            }
                                            fullWidth
                                            multiline
                                            rows={4}
                                            helperText="Brief description of what this system does"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </TabPanel>

                        {/* ── Tab: Spec ────────────────────────────────── */}
                        <TabPanel value={tabIndex} index={1}>
                            <Box className={classes.tabContent}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Owner"
                                            value={editSystem?.spec?.owner ?? ''}
                                            onChange={e => setFieldValue(['spec', 'owner'], e.target.value)}
                                            fullWidth
                                            select
                                        >
                                            {OWNERS.map(o => (
                                                <MenuItem key={o} value={o}>{o}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Backstage Lifecycle"
                                            value={editSystem?.spec?.lifecycle ?? ''}
                                            onChange={e => setFieldValue(['spec', 'lifecycle'], e.target.value)}
                                            fullWidth
                                            select
                                            helperText="Backstage spec.lifecycle field"
                                        >
                                            {BS_LIFECYCLES.map(l => (
                                                <MenuItem key={l} value={l}>{l}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Domain"
                                            value={editSystem?.spec?.domain ?? ''}
                                            onChange={e => setFieldValue(['spec', 'domain'], e.target.value)}
                                            fullWidth
                                            select
                                        >
                                            {DOMAINS.map(d => (
                                                <MenuItem key={d} value={d}>{d}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Box>
                        </TabPanel>

                        {/* ── Tab: Annotations ─────────────────────────── */}
                        <TabPanel value={tabIndex} index={2}>
                            <Box className={classes.tabContent}>
                                {/* Always-present annotations */}
                                <Box className={classes.alwaysBox}>
                                    <Typography variant="subtitle2" className={classes.sectionLabel}>
                                        System Identity
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Lifecycle Phase"
                                                helperText="itkom.pbs.ch/lifecycle-phase — controls which exit criteria apply"
                                                value={getAnno('lifecycle-phase')}
                                                onChange={e => setAnno('lifecycle-phase', e.target.value)}
                                                fullWidth
                                                select
                                            >
                                                {PHASES.map(p => (
                                                    <MenuItem key={p} value={p}>
                                                        {PHASE_LABELS[p]} ({p})
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Criticality"
                                                helperText="itkom.pbs.ch/criticality"
                                                value={getItkomAnno('criticality')}
                                                onChange={e => setItkomAnno('criticality', e.target.value)}
                                                fullWidth
                                                select
                                            >
                                                {CRITICALITY_OPTIONS.map(o => (
                                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Development Mode"
                                                helperText="itkom.pbs.ch/development-mode"
                                                value={getItkomAnno('development-mode')}
                                                onChange={e => setItkomAnno('development-mode', e.target.value)}
                                                fullWidth
                                                select
                                            >
                                                {DEV_MODE_OPTIONS.map(o => (
                                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Operations Mode"
                                                helperText="itkom.pbs.ch/operations-mode"
                                                value={getItkomAnno('operations-mode')}
                                                onChange={e => setItkomAnno('operations-mode', e.target.value)}
                                                fullWidth
                                                select
                                            >
                                                {OPS_MODE_OPTIONS.map(o => (
                                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                label="Has SLA"
                                                helperText="itkom.pbs.ch/has-sla"
                                                value={getItkomAnno('has-sla')}
                                                onChange={e => setItkomAnno('has-sla', e.target.value)}
                                                fullWidth
                                                select
                                            >
                                                {SLA_OPTIONS.map(o => (
                                                    <MenuItem key={o} value={o}>{o}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Involved Parties */}
                                <Box className={classes.alwaysBox} style={{ marginTop: 16 }}>
                                    <Typography variant="subtitle2" className={classes.sectionLabel}>
                                        Involved Parties
                                    </Typography>
                                    {getRoleAnnotations().map(({ roleSuffix, value }) => (
                                        <Box key={roleSuffix} className={classes.linkRow}>
                                            <TextField
                                                label="Role"
                                                value={roleSuffix}
                                                onChange={e => renameRoleAnno(roleSuffix, e.target.value)}
                                                style={{ flex: 1, minWidth: 180 }}
                                                size="small"
                                                select
                                            >
                                                {KNOWN_ROLES.map(r => (
                                                    <MenuItem key={r} value={r}>
                                                        {r.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                label="Entity refs"
                                                value={value}
                                                onChange={e => setRoleAnno(roleSuffix, e.target.value)}
                                                style={{ flex: 3 }}
                                                size="small"
                                                placeholder="user:default/clever, group:default/itkom"
                                                helperText="Comma-separated — user:default/... or group:default/..."
                                            />
                                            <Tooltip title="Remove role">
                                                <IconButton size="small" onClick={() => removeRoleAnno(roleSuffix)} style={{ marginTop: 4 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={addRoleAnno}
                                    >
                                        Add Role
                                    </Button>
                                </Box>

                                {/* Phase-specific annotation groups */}
                                {(PHASES).map(phase => {
                                    const annos = ANNO_GROUPS[phase];
                                    const isActive = currentPhase === phase;
                                    return (
                                        <Accordion
                                            key={phase}
                                            defaultExpanded={isActive}
                                            className={isActive ? classes.activeAccordion : undefined}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <div className={classes.phaseChipRow}>
                                                    <Chip
                                                        label={PHASE_LABELS[phase]}
                                                        size="small"
                                                        style={
                                                            isActive
                                                                ? { backgroundColor: PHASE_COLORS[phase], color: '#fff' }
                                                                : {}
                                                        }
                                                    />
                                                    <Typography variant="body2">
                                                        {PHASE_LABELS[phase]} Phase Criteria
                                                        {isActive && ' — current phase'}
                                                    </Typography>
                                                </div>
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.accordionDetails}>
                                                <Grid container spacing={2}>
                                                    {annos.map(anno => (
                                                        <Grid item xs={12} sm={6} md={4} key={anno.key}>
                                                            <TextField
                                                                label={anno.label}
                                                                helperText={`itkom.pbs.ch/${anno.key}`}
                                                                value={getAnno(anno.key)}
                                                                onChange={e => setAnno(anno.key, e.target.value)}
                                                                fullWidth
                                                                select
                                                                size="small"
                                                            >
                                                                {anno.options.map(opt => (
                                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        </TabPanel>

                        {/* ── Tab: Links ───────────────────────────────── */}
                        <TabPanel value={tabIndex} index={3}>
                            <Box className={classes.tabContent}>
                                {(editSystem?.metadata?.links ?? []).length === 0 ? (
                                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                        No links defined yet.
                                    </Typography>
                                ) : (
                                    (editSystem?.metadata?.links ?? []).map((link, i) => (
                                        <Box key={i} className={classes.linkRow}>
                                            <TextField
                                                label="URL"
                                                value={link.url}
                                                onChange={e => updateLink(i, 'url', e.target.value)}
                                                style={{ flex: 3 }}
                                                size="small"
                                                placeholder="https://..."
                                            />
                                            <TextField
                                                label="Title"
                                                value={link.title}
                                                onChange={e => updateLink(i, 'title', e.target.value)}
                                                style={{ flex: 2 }}
                                                size="small"
                                            />
                                            <TextField
                                                label="Icon"
                                                value={link.icon ?? 'website'}
                                                onChange={e => updateLink(i, 'icon', e.target.value)}
                                                style={{ flex: 1, minWidth: 120 }}
                                                size="small"
                                                select
                                            >
                                                {LINK_ICONS.map(icon => (
                                                    <MenuItem key={icon} value={icon}>{icon}</MenuItem>
                                                ))}
                                            </TextField>
                                            <Tooltip title="Remove link">
                                                <IconButton size="small" onClick={() => removeLink(i)} style={{ marginTop: 4 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))
                                )}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addLink}
                                >
                                    Add Link
                                </Button>
                            </Box>
                        </TabPanel>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={closeEdit}>Cancel</Button>
                        <Button onClick={saveEdit} color="primary" variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Content>
        </Page>
    );
}

export default SystemEditorPage;
